import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import SEOHelmet from '../components/SEOHelmet';
import VendorCard from '../components/VendorCard';
import ReviewsModal from '../components/ReviewsModal';
import apiService from '../services/api';
import { generateCanonicalUrl, generateBreadcrumbs, categoryLabels, generatePageKeywords } from '../utils/seoUtils';

// Keep category names locally for now
const categoryNames = {
  "catering": "Catering Services",
  "decorators": "Decorators", 
  "bands": "Bands & Banjo",
  "photographers": "Photographers & Content Creators",
  "makeup": "Makeup Artists",
  "venues": "Venues",
  "dj": "DJ & Music",
  "invitations": "Invitation Cards",
  "fashion": "Function Wear",
  "decorations": "Stage & Entrance Decorations",
  "cakes": "Cakes & Gift Shops",
  "anchors": "Anchors & Hosts",
  "entertainment": "Entertainment & Extras",
  "sweets": "Sweets & Snacks"
};

const CategoryPage = () => {
  const { categorySlug } = useParams();
  const [vendors, setVendors] = useState([]);
  const [filteredVendors, setFilteredVendors] = useState([]);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [showReviewsModal, setShowReviewsModal] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState({
    location: '',
    sortBy: 'name'
  });
  
console.log(categorySlug);
  const categoryTitle = categoryNames[category] || 'Vendors';

  useEffect(() => {
    const loadVendors = async () => {
      setLoading(true);
      try {
        // Map frontend category to backend category
        const categoryMap = {
          "catering-services": "catering",
          "decorators": "decorators",
          "bands-banjo": "bands",
          "photographers-content-creators": "photographers",
          "makeup-artists": "makeup",
          "venues": "venues",
          "dj-music": "dj",
          "invitation-cards": "invitations",
          "function-wear": "fashion",
          "stage-entrance-decorations": "decorations",
          "cakes-gift-shops": "cakes",
          "anchors-hosts": "anchors",
          "entertainment-extras": "entertainment",
          "sweets-snacks": "sweets"
        };

        const backendCategory = categoryMap[categorySlug] || categorySlug;
        
        // Fetch vendors from API - need to get category ID first
        const categoryResponse = await apiService.api.get(`/categories`);
        const categories = categoryResponse.data.categories || [];
        const categoryObj = categories.find(cat => cat.slug === backendCategory);
        
        if (!categoryObj) {
          setVendors([]);
          setCategory(null);
          return;
        }

        setCategory(categoryObj);

        const response = await apiService.getVendors({ 
          category: backendCategory,
          sort: 'featured',
          limit: 1000  // Set a high limit to get all vendors
        });
        
        if (response.success) {
          // Transform API data to match frontend expectations
          const transformedVendors = response.vendors.map(vendor => ({
            id: vendor._id,
            name: vendor.businessName,
            businessName: vendor.businessName,
            category: vendor.category,
            location: vendor.location?.city || vendor.location?.address || 'Not specified',
            image: vendor.images?.businessImage?.url || vendor.images?.coverImage?.url || vendor.images?.coverImage || 'https://images.unsplash.com/photo-1560472355-536de3962603?w=400&h=300&fit=crop',
            images: vendor.images, // Pass through the full images object
            reviewCount: vendor.ratings?.totalReviews || 0,
            description: vendor.description,
            experience: vendor.businessInfo?.experience || 'Not specified',
            contact: {
              phone: vendor.contact?.phone,
              email: vendor.contact?.email,
              website: vendor.contact?.website
            },
            priceRange: vendor.pricing?.startingPrice ? `₹${vendor.pricing.startingPrice}+` : 'Contact for pricing',
            services: vendor.services?.map(s => s.name) || [],
            isSponsored: vendor.subscription?.isSponsored || false
          }));
          
          setVendors(transformedVendors);
          setFilteredVendors(transformedVendors);
        } else {
          setVendors([]);
          setFilteredVendors([]);
        }
      } catch (error) {
        console.error('Error loading vendors:', error);
        setVendors([]);
        setFilteredVendors([]);
      } finally {
        setLoading(false);
      }
    };

    loadVendors();
  }, [categorySlug]);

  // Filter and sort vendors
  useEffect(() => {
    let filtered = [...vendors];

    // Filter by location
    if (filters.location) {
      filtered = filtered.filter(vendor => 
        vendor.location?.toLowerCase().includes(filters.location.toLowerCase())
      );
    }

    // Sort vendors - sponsored vendors first, then by selected criteria
    filtered.sort((a, b) => {
      // Check if either vendor is sponsored
      const aSponsored = a.isSponsored || a.subscription?.isSponsored || false;
      const bSponsored = b.isSponsored || b.subscription?.isSponsored || false;
      
      // If one is sponsored and the other isn't, sponsored comes first
      if (aSponsored && !bSponsored) return -1;
      if (!aSponsored && bSponsored) return 1;
      
      // If both are sponsored or both are not sponsored, sort by selected criteria
      switch (filters.sortBy) {
        case 'name':
          return a.businessName.localeCompare(b.businessName);
        case 'location':
          return a.location.localeCompare(b.location);
        default:
          return 0;
      }
    });

    setFilteredVendors(filtered);
  }, [vendors, filters]);

  const handleViewReviews = (vendor) => {
    console.log('👀 Viewing reviews for vendor:', vendor.businessName);
    setSelectedVendor(vendor);
    setShowReviewsModal(true);
  };

  const handleCloseReviews = () => {
    setShowReviewsModal(false);
    setSelectedVendor(null);
  };
console.log(vendors);

  // Handle when a new review is added
  const handleReviewAdded = async (updatedVendor) => {
    console.log('🔄 Review added, refreshing vendor data...');
    
    try {
      // Refresh the specific vendor's data
      const response = await apiService.get(`/vendors/${updatedVendor.slug}`);
      
      if (response.success) {
        const refreshedVendor = response.vendor;
        
        // Update the vendors list with the refreshed data
        setVendors(prevVendors => 
          prevVendors.map(vendor => 
            vendor._id === refreshedVendor._id ? refreshedVendor : vendor
          )
        );
        
        // Update the selected vendor for the modal
        setSelectedVendor(refreshedVendor);
        
        console.log('✅ Vendor data refreshed:', {
          name: refreshedVendor.businessName,
          reviewCount: refreshedVendor.ratings?.count
        });
      }
    } catch (error) {
      console.error('❌ Error refreshing vendor data:', error);
      // Fallback: reload the page to refresh vendor list
      window.location.reload();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading {categoryTitle}...</p>
        </div>
      </div>
    );
  }

  const categoryName = categoryLabels[categorySlug] || categoryTitle;
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": `${categoryName} - Ceremonzy`,
    "description": `Find the best ${categoryName.toLowerCase()} vendors for your wedding ceremony. Browse verified professionals with reviews and ratings.`,
    "url": generateCanonicalUrl(`/category/${categorySlug}`),
    "breadcrumb": generateBreadcrumbs(`/category/${categorySlug}`, {
      'category': 'Categories',
      [categorySlug]: categoryName
    }),
    "mainEntity": {
      "@type": "ItemList",
      "numberOfItems": filteredVendors.length,
      "itemListElement": filteredVendors.slice(0, 10).map((vendor, index) => ({
        "@type": "LocalBusiness",
        "position": index + 1,
        "name": vendor.businessName || vendor.name,
        "description": vendor.description,
        "aggregateRating": vendor.averageRating ? {
          "@type": "AggregateRating",
          "ratingValue": vendor.averageRating,
          "reviewCount": vendor.reviewCount || 0
        } : undefined
      }))
    }
  };

  return (
    <>
      <SEOHelmet
        title={`${categoryName} in India - Find Best Wedding ${categoryName} | Ceremonzy`}
        description={`Discover top-rated ${categoryName.toLowerCase()} for your wedding ceremony. Compare prices, read reviews, and book verified ${categoryName.toLowerCase()} vendors on Ceremonzy.`}
        keywords={generatePageKeywords([categoryName.toLowerCase(), `wedding ${categoryName.toLowerCase()}`, `${categoryName.toLowerCase()} vendors`, `${categoryName.toLowerCase()} services`])}
        url={`/category/${categorySlug}`}
        structuredData={structuredData}
      />
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-red-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-6 sm:mb-8">
            <div className="flex flex-col space-y-4 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-center sm:text-left">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">{categoryTitle}</h1>
                <p className="text-sm sm:text-base text-gray-600">
                  Find the best {categoryTitle.toLowerCase()} vendors in Vasai-Virar area
                </p>
              </div>
            <div className="flex justify-center sm:justify-end">
              <span className="bg-pink-100 text-pink-800 px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium">
                {filteredVendors.length} of {vendors.length} vendors available
              </span>
            </div>
          </div>
        </div>

        {/* Filter Panel */}
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-6 sm:mb-8">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Filter & Sort</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Location Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Location
              </label>
              <select
                value={filters.location}
                onChange={(e) => setFilters(prev => ({ ...prev, location: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-maroon focus:border-maroon"
              >
                <option value="">All Locations</option>
                <option value="Virar">Virar</option>
                <option value="Vasai">Vasai</option>
                <option value="Nalasopara">Nalasopara</option>
                <option value="Others">Others</option>
              </select>
            </div>


          </div>

          {/* Clear Filters */}
          <div className="mt-4">
            <button
              onClick={() => setFilters({ location: '', sortBy: 'name' })}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 underline"
            >
              Clear All Filters
            </button>
          </div>
        </div>

        {/* All Vendors in Single Column */}
        {filteredVendors.length > 0 && (
          <div>
            <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-4 px-2 sm:px-0">
              {filteredVendors.length === vendors.length ? 'All' : 'Filtered'} {categoryTitle}
            </h2>
            <div className="flex flex-col gap-4 sm:gap-6">
              {filteredVendors.map((vendor) => (
                <VendorCard
                  key={vendor.id}
                  vendor={vendor}
                  onViewReviews={handleViewReviews}
                  onReviewAdded={handleReviewAdded}
                />
              ))}
            </div>
          </div>
        )}

        {/* No Vendors Found */}
        {filteredVendors.length === 0 && vendors.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-6 sm:p-8 text-center mx-2 sm:mx-0">
            <div className="text-gray-400 mb-4">
              <svg className="w-12 h-12 sm:w-16 sm:h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No vendors match your filters</h3>
            <p className="text-sm sm:text-base text-gray-600 mb-4">
              Try adjusting your filters to find more {categoryTitle.toLowerCase()} vendors.
            </p>
            <button
              onClick={() => setFilters({ location: '', sortBy: 'name' })}
              className="bg-maroon text-white px-4 py-2 rounded-lg hover:bg-red-900 transition-colors"
            >
              Clear All Filters
            </button>
          </div>
        )}
        
        {vendors.length === 0 && (
          <div className="bg-white rounded-lg shadow-md p-6 sm:p-8 text-center mx-2 sm:mx-0">
            <div className="text-gray-400 mb-4">
              <svg className="w-12 h-12 sm:w-16 sm:h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.5-.881-6.172-2.328M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No vendors found</h3>
            <p className="text-sm sm:text-base text-gray-600">
              Try adjusting your filters to find {categoryTitle.toLowerCase()} vendors in your area.
            </p>
          </div>
        )}

        {/* Legal Disclaimer Footer */}
        <div className="mt-8 p-6 bg-gray-50 rounded-lg border border-gray-200 mx-2 sm:mx-0">
          <div className="text-center">
            <h4 className="text-lg font-semibold text-gray-800 mb-3">Platform Disclaimer</h4>
            <div className="text-sm text-gray-600 space-y-2">
              <p>
                <strong>Intermediary Notice:</strong> Ceremonzy operates as an intermediary platform under the Information Technology Act, 2000. 
                We connect users with service providers but do not provide wedding services directly.
              </p>
              <p>
                <strong>User Content:</strong> All vendor profiles, reviews, ratings, and user-generated content reflect individual opinions and experiences. 
                We do not verify, endorse, or guarantee the accuracy of such content.
              </p>
              <p>
                <strong>Service Quality:</strong> We are not responsible for the quality, timeliness, or performance of services provided by vendors. 
                Users engage with vendors at their own discretion and risk.
              </p>
              <div className="mt-4 pt-3 border-t border-gray-300">
                <p className="text-xs text-gray-500">
                  For content issues or complaints, visit our <a href="/grievance-policy" className="text-blue-600 hover:underline">Grievance Policy</a> | 
                  By using this platform, you agree to our <a href="/terms-of-use" className="text-blue-600 hover:underline">Terms of Use</a> and <a href="/privacy-policy" className="text-blue-600 hover:underline">Privacy Policy</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Reviews Modal */}
      {showReviewsModal && selectedVendor && (
        <ReviewsModal 
          vendor={selectedVendor}
          isOpen={showReviewsModal}
          onClose={handleCloseReviews}
          onReviewAdded={handleReviewAdded}
        />
      )}
      </div>
    </>
  );
};

export default CategoryPage;